import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { overrideMemberId } from "modules/patient/helper/bookingFor";
import PropTypes from "prop-types";

import TopBar from "components/common/TopBar";
import RichTextArea from "components/common/RichTextArea";
import EventService from "service/event/EventService";
import AuthenticationService from "service/auth/AuthenticationService";
import EnvironmentService from "service/environment/EnvironmentService";
import AppointmentService from "modules/appointments/service/AppointmentService";
import PatientService from "modules/patient/service/PatientService";
import HealthPlanService from "../service/HealthPlanService";
import VersionHistoryPanel from "./VersionHistoryPanel";
import HpConfirmDialog from "./HpConfirmDialog";
import HpFoodList from "./HpFoodList";
import cleverTapAB from "util/cleverTapAB";
import readLatestSchedule from "modules/patient/sections/dailySchedule/latestSchedule";
import { formatTimeAmPm, normalizeActivity } from "modules/patient/sections/dailySchedule/data";
import HealthPlanProfilePanels from "modules/patient/components/HealthPlanProfilePanels";
import {
  buildSaveBody,
  descHtml,
  foodFlagsOf,
  foodListIndex,
  formatPlanDate,
  dataFilter,
  GUIDELINE_UNITS,
  isTimeValid,
  levelNumber,
  newPlanSkeleton,
  nextVersionPlan,
  normalizePlan,
  templateSections,
  toVersionRows,
  validatePlan,
} from "../helper/healthPlanMappers";
import "../styles/healthPlan.scss";
import isScrollbarPress from "components/common/isScrollbarPress";

/**
 * The Health-plan editor (Figma flow board 3141:6145) — LIVE against bridge's
 * v4 endpoints (see APIConfig.healthPlan). Route
 * /navigo-module/health-plan/:appointmentId; the appointment resolves the
 * patient the plan belongs to.
 *
 * Wire model: a plan version ("Level N" on the wire, "Version N" in this UI)
 * carries data keyed by category id → { items, guidelines }. Category tabs and
 * the sidebar's addable section list are server-driven (/v4/category,
 * /v4/item/category/{id}). Publish is the same save call with published:true.
 * A published version is READ-ONLY (bridge disables everything) — editing
 * continues on a new version that carries the data forward.
 *
 * Images saved on the plan are SHOWN, display-only, where bridge shows them
 * (2026-09-17) — never uploaded from here; the fields round-trip untouched.
 *
 * Legacy parity (checked against bridge's live v4 screen, 2026-09-17 — see
 * scripts/verify/hp-legacy-parity.js): the empty-Medical warning guards drafts
 * too; an empty plan is refused; times are "h:mm am/pm" (lower case, ≤ 8
 * characters); guidelines use bridge's four units and digit-only ranges and a
 * picked one goes on top with its ranges; instructions suggest; an option is
 * added once per section; "Fetch plan" templates on Exercise / Medical /
 * Meditation; a section shows the patient's daily-schedule time; new versions
 * follow addNewLevel (below Version 3 → an empty Version 3).
 *
 * Publish notifications (bridge's growthEvents, PRODUCTION-gated): once the
 * post-publish PDF is ready, a WhatsApp event fires (HP_PUBLISHED /
 * HP_3_PUBLISHED for level 3); 03XXAZ patients also get the referral-feedback
 * SMS; the CRM disposition is filed; 02XXAB patients get bridge's CleverTap
 * A/B event (util/cleverTapAB — started only on a production 02XXAB plan).
 */
const HP_FEEDBACK_LINK =
  "https://api.simplesat.io/api/rating/yuT3EGBcBca7OcC9H5FeZqGi0ybkBmEQEH8Pwk3DNA4/?source=generic_embed";
const SMS_BODY =
  "Please rate your experience based on the health plan received from your Dr {0} nutrition team at Zyla. {1}";

/** Reorder helper: returns a copy of `arr` with the item moved from → to. */
const moveInArray = (arr, from, to) => {
  const next = [...arr];
  const [x] = next.splice(from, 1);
  next.splice(to, 0, x);
  return next;
};

const Chev = () => (
  <svg className="hp-chev" viewBox="0 0 6 3" width="6" height="3" fill="none"
    stroke="currentColor" strokeWidth="1" strokeLinecap="round" aria-hidden="true">
    <path d="M0.5 0.5L3 2.5 5.5 0.5" />
  </svg>
);

const DescLabel = () => (
  <div className="hp-desc-label">
    <span>description</span>
    <span className="hp-desc-menu" aria-hidden="true">&#9776;</span>
  </div>
);

/** Sanitised HTML description (options/guidelines store draft-js HTML). */
const DescriptionHtml = ({ html }) => {
  if (!html) return null;
  return (
    // eslint-disable-next-line react/no-danger
    <div className="hp-desc" dangerouslySetInnerHTML={{ __html: descHtml(html) }} />
  );
};
DescriptionHtml.propTypes = { html: PropTypes.string };

/** Closes an open picker on a press anywhere outside `ref`. Its list floats
 *  over the editors below, and without this it stayed open until something was
 *  picked or Escape was pressed.
 *
 *  A press is the whole gesture: down AND up outside. Closing on mousedown
 *  alone collapsed the list the instant a section's drag grip was grabbed, so
 *  every section below it jumped up under the cursor as the drag began. (A
 *  native drag fires no mouseup, so dragging leaves the picker open; the next
 *  mousedown starts afresh.) Capture phase, so an editor that stops the events
 *  from bubbling still closes it. */
const useCloseOnOutsidePress = (ref, open, setOpen) => {
  useEffect(() => {
    if (!open) return undefined;
    const outside = (e) => !!ref.current && !ref.current.contains(e.target);
    let downOutside = false;
    const onDown = (e) => {
      /* A SCROLLBAR GRAB IS NOT THE START OF A PRESS AWAY. These lists are
         inline, so a scroll never strands them and nothing here listens for
         one — but the gesture that drags the bar is a mousedown and a mouseup
         outside, which is precisely what this hook closes on, so the list died
         on release (Nittin, 2026-09-24). */
      downOutside = outside(e) && !isScrollbarPress(e);
    };
    const onUp = (e) => {
      if (downOutside && outside(e)) setOpen(false);
      downOutside = false;
    };
    document.addEventListener("mousedown", onDown, true);
    document.addEventListener("mouseup", onUp, true);
    return () => {
      document.removeEventListener("mousedown", onDown, true);
      document.removeEventListener("mouseup", onUp, true);
    };
  }, [ref, open, setOpen]);
};

/** "+ Add guideline" — pick from the category's master guidelines. `disabled`
 *  hides it (a read-only version); `inert` shows it but locks it (the host is
 *  busy — e.g. generating), closing a list that was open. */
const AddGuideline = ({ masters, existingNames, disabled, inert, onAdd }) => {
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);
  useEffect(() => {
    if (inert) setOpen(false);
  }, [inert]);
  useCloseOnOutsidePress(boxRef, open, setOpen);
  if (disabled) return null;
  const available = (masters || []).filter(
    (m) => m && m.name && existingNames.indexOf(String(m.name).toLowerCase()) === -1
  );
  if (!open || inert) {
    return (
      <button type="button" className="hp-add-option" disabled={inert} onClick={() => setOpen(true)}>
        + Add guideline
      </button>
    );
  }
  return (
    <div className="hp-opt-search" ref={boxRef}>
      {available.length === 0 ? (
        <p className="hp-empty-note">Every guideline for this category is already added.</p>
      ) : (
        <ul className="hp-opt-results is-static" role="listbox">
          {available.map((m) => (
            <li key={m.id || m.name}>
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  onAdd(m);
                }}
              >
                {m.name}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
AddGuideline.propTypes = {
  masters: PropTypes.array,
  existingNames: PropTypes.array.isRequired,
  disabled: PropTypes.bool,
  inert: PropTypes.bool,
  onAdd: PropTypes.func.isRequired,
};

/** A whole-number field kept numeric on the wire; "" while the box is empty. */
const toCount = (raw) => {
  const digits = String(raw).replace(/[^0-9]/g, "");
  return digits === "" ? "" : Number(digits);
};

/** Unit entries arrive as bare strings OR objects — one reader for both. */
/* Bridge's recipe units are [{ key, value }]: its select SHOWS `value` and
   STORES `key` (Recipe.js: <MenuItem value={e.key}>{e.value}</MenuItem>). A bare
   string is both. */
const unitKey = (u) =>
  typeof u === "string" ? u : (u && (u.key != null && u.key !== "" ? String(u.key) : u.value || u.name || u.unit)) || "";
const unitText = (u) =>
  typeof u === "string" ? u : (u && (u.value || u.name || u.unit || u.key)) || "";

/** "+ Add ingredient" — bridge's ingredient autocomplete, filtered client-side
 *  (bridge fetches the whole master list too, so there's nothing to search for). */
const AddIngredient = ({ masters, disabled, inert, onAdd }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  useEffect(() => {
    if (!inert) return;
    setOpen(false);
    setQuery("");
  }, [inert]);
  if (disabled) return null;

  const q = query.trim().toLowerCase();
  const matches = q
    ? (masters || [])
        .filter((m) => String(m.name || "").toLowerCase().indexOf(q) !== -1)
        .slice(0, 8)
    : [];

  return open && !inert ? (
    <div className="hp-ing-add">
      <input
        className="hp-input"
        placeholder="Search ingredients…"
        value={query}
        autoFocus
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={(e) => e.key === "Escape" && setOpen(false)}
        // Close on click-away. Deferred so a click on a result still lands —
        // an always-open list floats over the Method editor below, where a
        // stray click would silently add an ingredient.
        onBlur={() => setTimeout(() => setOpen(false), 150)}
      />
      {/* Results only once something is typed, like AddOption. */}
      {q !== "" && (
        <ul className="hp-opt-results" role="listbox">
          {matches.length === 0 ? (
            <li className="hp-note">
              {(masters || []).length
                ? "No ingredient matches that."
                : "Ingredient list unavailable."}
            </li>
          ) : (
            matches.map((m) => (
              <li key={m.id || m.name}>
                <button
                  type="button"
                  role="option"
                  aria-selected="false"
                  onClick={() => {
                    // Keep the master's id/category — the wire carries them
                    // ({id,name,category,amount,unit,instruction}), and without
                    // the id the row loses its link back to the ingredient.
                    onAdd({
                      ...(m.id ? { id: m.id } : {}),
                      name: m.name,
                      ...(m.category ? { category: m.category } : {}),
                      instruction: "",
                      amount: "",
                      unit: "",
                    });
                    setQuery("");
                    setOpen(false);
                  }}
                >
                  {m.name}
                </button>
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  ) : (
    <button type="button" className="hp-add-link" disabled={inert} onClick={() => setOpen(true)}>
      + Add ingredient
    </button>
  );
};
AddIngredient.propTypes = {
  masters: PropTypes.array,
  disabled: PropTypes.bool,
  inert: PropTypes.bool,
  onAdd: PropTypes.func.isRequired,
};

/**
 * A recipe attached to a nutrition option. Editable to the SAME degree bridge's
 * Recipe.js is, and no further:
 *   editable — servingSize, cookingTime, method (rich text), and per ingredient
 *              instruction / amount / unit (+ add & remove ingredient)
 *   NOT      — recipe name, recipe image, ingredient name (bridge renders that
 *              input but its onChange is commented out, so it's inert there),
 *              and adding/removing a whole recipe (bridge's control is
 *              commented out AND disabled).
 * Bridge leaves the method editor live even on a published plan; that's a bug
 * over there (its save path is blocked anyway), so we gate it like everything else.
 */
/**
 * A saved image, DISPLAY-ONLY (Nittin, 2026-09-17 — reverses 2026-07-23's "no
 * photos": the images already saved on the plan are shown, as legacy bridge's
 * health plan shows them; there is still no upload and no drop zone).
 *
 * The raw URL, as legacy uses it — these are public upload URLs, no document
 * token. Nothing at all is drawn when there is no URL or it fails to load: no
 * empty box that reads as an upload zone, no broken-image icon.
 */
const HpImage = ({ src, alt, className }) => {
  const url = typeof src === "string" ? src.trim() : "";
  const [failedUrl, setFailedUrl] = useState("");
  if (!url || failedUrl === url) return null;
  return (
    <span className={`hp-img ${className || ""}`}>
      <img src={url} alt={alt || ""} loading="lazy" onError={() => setFailedUrl(url)} />
    </span>
  );
};
HpImage.propTypes = { src: PropTypes.string, alt: PropTypes.string, className: PropTypes.string };

const RecipeBlock = ({
  recipe,
  readOnly,
  disabled,
  units,
  ingredientMasters,
  editKey,
  onPatch,
  onPatchIngredient,
  onAddIngredient,
  onRemoveIngredient,
  service,
}) => {
  const ingredients = recipe.ingredients || [];
  return (
    <div className="hp-recipe">
      <h4 className="hp-recipe-title">{recipe.name}</h4>
      <div className="hp-recipe-grid">
        <div className="hp-recipe-fields">
          <div className="hp-small-field w-180">
            <span className="hp-field-label">Serving size</span>
            <input
              className="hp-input"
              value={recipe.servingSize == null ? "" : recipe.servingSize}
              readOnly={readOnly || disabled}
              // Whole numbers, kept as NUMBERS: the wire types these numerically
              // (servingSize:1, cookingTime:15) and saving "3" as a string is a
              // type regression a typed server DTO can reject.
              onChange={(e) => onPatch({ servingSize: toCount(e.target.value) })}
            />
          </div>
          <div className="hp-small-field w-200">
            <span className="hp-field-label">Cooking time (in mins)</span>
            <input
              className="hp-input"
              value={recipe.cookingTime == null ? "" : recipe.cookingTime}
              readOnly={readOnly || disabled}
              onChange={(e) => onPatch({ cookingTime: toCount(e.target.value) })}
            />
          </div>
        </div>
        {/* 220x150 at the row's right (Figma 3187:7935). */}
        <HpImage src={recipe.image} alt={recipe.name} className="is-recipe" />
      </div>

      {(ingredients.length > 0 || !readOnly) && (
        <>
          <div className="hp-subhead-row">
            <span className="hp-subhead">
              Ingredients
              <span className="hp-count">({ingredients.length})</span>
            </span>
          </div>
          {ingredients.map((ing, i) => (
            <div className="hp-ing-row" key={i}>
              {/* Ingredient name is display-only — bridge's equivalent input has
                  its onChange commented out, so it can't be edited there either. */}
              <div className="hp-small-field w-275">
                <span className="hp-field-label">Ingredient</span>
                <input
                  className="hp-input"
                  value={(ing.ingredient && ing.ingredient.name) || ing.name || ""}
                  readOnly
                />
              </div>
              <div className="hp-small-field w-275">
                <span className="hp-field-label">Instruction</span>
                <InstructionInput
                  value={ing.instruction || ""}
                  readOnly={readOnly || disabled}
                  service={service}
                  onChange={(text) => onPatchIngredient(i, { instruction: text })}
                />
              </div>
              <div className="hp-small-field w-157">
                <span className="hp-field-label">Amount</span>
                <input
                  className="hp-input"
                  value={ing.amount == null ? "" : ing.amount}
                  readOnly={readOnly || disabled}
                  // Bridge allows digits plus . / and -, for "1/2", "1-2" etc.
                  onChange={(e) =>
                    onPatchIngredient(i, { amount: e.target.value.replace(/[^0-9./-]/g, "") })
                  }
                />
              </div>
              <div className="hp-small-field w-177">
                <span className="hp-field-label">Unit</span>
                <select
                  className="hp-input hp-unit-select"
                  value={ing.unit || ""}
                  disabled={readOnly || disabled}
                  onChange={(e) => onPatchIngredient(i, { unit: e.target.value })}
                >
                  <option value="">Select unit</option>
                  {units.map((u) => (
                    <option key={unitKey(u)} value={unitKey(u)}>
                      {unitText(u)}
                    </option>
                  ))}
                  {/* A saved unit that is not one of the keys stays shown as it
                      is (bridge would show the field blank); it is only
                      changed when someone picks another. */}
                  {ing.unit && !units.some((u) => unitKey(u) === ing.unit) && (
                    <option value={ing.unit}>{ing.unit}</option>
                  )}
                </select>
              </div>
              {!readOnly && (
                <button
                  type="button"
                  className="hp-ing-remove"
                  title="Remove ingredient"
                  aria-label="Remove ingredient"
                  disabled={disabled}
                  onClick={() => onRemoveIngredient(i)}
                >
                  &minus;
                </button>
              )}
            </div>
          ))}
          <AddIngredient
            masters={ingredientMasters}
            disabled={readOnly}
            inert={disabled}
            onAdd={onAddIngredient}
          />
        </>
      )}

      {(recipe.method || !readOnly) && (
        <div className="hp-meta">
          <div className="hp-meta-key">Method:</div>
          {readOnly ? (
            <DescriptionHtml html={recipe.method} />
          ) : (
            <RichTextArea
              key={editKey}
              defaultHtml={descHtml(recipe.method)}
              placeholder="Describe the method…"
              disabled={disabled}
              onChange={(html) => onPatch({ method: html })}
            />
          )}
        </div>
      )}
    </div>
  );
};
RecipeBlock.propTypes = {
  recipe: PropTypes.object.isRequired,
  /** A read-only version: shows the recipe as text, no edit controls. */
  readOnly: PropTypes.bool,
  /** Busy (e.g. generating): every control stays in place but inert. */
  disabled: PropTypes.bool,
  units: PropTypes.array,
  ingredientMasters: PropTypes.array,
  /** Remount key for the uncontrolled rich-text method editor. */
  editKey: PropTypes.string,
  onPatch: PropTypes.func.isRequired,
  onPatchIngredient: PropTypes.func.isRequired,
  onAddIngredient: PropTypes.func.isRequired,
  onRemoveIngredient: PropTypes.func.isRequired,
  /** HealthPlanService — the instruction suggestions. */
  service: PropTypes.object.isRequired,
};
RecipeBlock.defaultProps = { units: [], ingredientMasters: [] };

/**
 * An option's recipes as a "Recipes" row of chips, each opening and closing its
 * own recipe below (Figma 3187:8046 closed, 3187:7892 open — Nittin,
 * 2026-09-17). All closed to begin with; any number may be open, shown in the
 * option's order. A closed recipe stays MOUNTED (hidden), so its editors keep
 * their state and the generate lock still reaches them — opening one is only a
 * view change, allowed on any version and while the screen is locked.
 */
const OptionRecipes = ({ recipes, idBase, renderRecipe }) => {
  const [open, setOpen] = useState([]);
  const toggle = (i) =>
    setOpen((prev) => (prev.includes(i) ? prev.filter((x) => x !== i) : [...prev, i]));
  return (
    <>
      <div className="hp-recipe-chips">
        <span className="hp-recipe-chips-label">Recipes</span>
        <div className="hp-recipe-chip-list">
          {recipes.map((r, i) => {
            const on = open.includes(i);
            return (
              <button
                key={`${r.name}-${i}`}
                type="button"
                className={`hp-recipe-chip hp-recipe-chip-btn${on ? " is-open" : ""}`}
                aria-expanded={on}
                aria-controls={`${idBase}-${i}`}
                onClick={() => toggle(i)}
              >
                <span className="hp-recipe-chip-name">{r.name || `Recipe ${i + 1}`}</span>
                <svg width="8" height="5" viewBox="0 0 8 5" fill="none" aria-hidden="true">
                  <path
                    d={on ? "M1 4L4 1L7 4" : "M1 1L4 4L7 1"}
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            );
          })}
        </div>
      </div>
      {recipes.map((r, i) => (
        <div key={`${r.name}-${i}`} id={`${idBase}-${i}`} className="hp-recipe-panel" hidden={!open.includes(i)}>
          {renderRecipe(r, i)}
        </div>
      ))}
    </>
  );
};

OptionRecipes.propTypes = {
  recipes: PropTypes.arrayOf(PropTypes.object).isRequired,
  /** Unique per option on the page — the panels' DOM ids. */
  idBase: PropTypes.string.isRequired,
  renderRecipe: PropTypes.func.isRequired,
};

/** "+ Add option" — bridge's autocomplete: search options for the item, pick one. */
/**
 * The option-search response, as a list.
 *
 * TWO BUGS LIVED IN THE ONE LINE THIS REPLACES —
 * `Array.isArray(res) ? res.slice(0, 8) : []`:
 *
 *   · `.slice(0, 8)` threw away the ninth match onward, so the same query on
 *     the same patient offered fewer options here than on bridge, which lists
 *     whatever came back (`setState({ options: data })`). That was the report
 *     (Nittin, 2026-09-12, e.g. the Pre-workout section). The list is
 *     `max-height: 220px; overflow-y: auto`, so it scrolls rather than growing.
 *   · the bare `Array.isArray` test turned any WRAPPED payload into an empty
 *     list instead of reading the array inside it — the "and sometimes it shows
 *     no options" half.
 *
 * The request was never the difference: both frontends call the same
 * item-scoped /v4/option/search/opt/{itemId}?query={q} (bridge's
 * getOptionsByItem) and neither encodes the query — String.prototype.format is
 * a plain interpolation in both repos.
 */
const optionResults = (res) => {
  if (Array.isArray(res)) return res;
  if (!res || typeof res !== "object") return [];
  /* The shapes this API family actually uses elsewhere in phr. */
  const wrapped = res.options || res.data || res.items || res.result;
  return Array.isArray(wrapped) ? wrapped : [];
};

/**
 * A recipe ingredient's instruction: free text, as in bridge, with bridge's
 * suggestions while typing (Recipe.js getinstruction → /v1/instructions).
 * Picking one fills the field; typing on is still allowed.
 */
let instructionListSeq = 0;
const InstructionInput = ({ value, readOnly, service, onChange }) => {
  const listId = useRef(null);
  if (!listId.current) listId.current = `hp-ins-list-${(instructionListSeq += 1)}`;
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const timer = useRef(null);
  const seq = useRef(0);
  const boxRef = useRef(null);
  useEffect(
    () => () => {
      seq.current += 1;
      if (timer.current) clearTimeout(timer.current);
    },
    []
  );
  // Locked or read-only: drop an open list and any answer still on its way.
  useEffect(() => {
    if (!readOnly) return;
    seq.current += 1;
    if (timer.current) clearTimeout(timer.current);
    setOpen(false);
    setResults([]);
  }, [readOnly]);
  useCloseOnOutsidePress(boxRef, open, setOpen);

  const lookUp = (text) => {
    const n = ++seq.current;
    if (timer.current) clearTimeout(timer.current);
    if (!text.trim()) {
      setResults([]);
      setOpen(false);
      return;
    }
    timer.current = setTimeout(() => {
      service.searchInstructions(
        text.trim(),
        (res) => {
          if (n !== seq.current) return;
          const names = [];
          (Array.isArray(res) ? res : []).forEach((r) => {
            const name = String((r && typeof r === "object" ? r.name : r) || "").trim();
            if (name && names.indexOf(name) === -1) names.push(name);
          });
          setResults(names.slice(0, 8));
          setActive(-1);
          setOpen(names.length > 0);
        },
        () => {
          if (n !== seq.current) return;
          setResults([]);
          setOpen(false);
        }
      );
    }, 300);
  };
  const choose = (name) => {
    seq.current += 1;
    onChange(name);
    setOpen(false);
    setResults([]);
  };

  return (
    <div className="hp-ins" ref={boxRef}>
      <input
        className="hp-input"
        value={value}
        readOnly={readOnly}
        role="combobox"
        aria-label="Instruction"
        aria-autocomplete="list"
        aria-expanded={open}
        aria-controls={listId.current}
        onChange={(e) => {
          onChange(e.target.value);
          lookUp(e.target.value);
        }}
        onKeyDown={(e) => {
          if (!open) return;
          if (e.key === "ArrowDown") {
            e.preventDefault();
            setActive((a) => Math.min(results.length - 1, a + 1));
          } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setActive((a) => Math.max(0, a - 1));
          } else if (e.key === "Enter" && active >= 0) {
            e.preventDefault();
            choose(results[active]);
          } else if (e.key === "Escape") {
            setOpen(false);
          }
        }}
      />
      {open && !readOnly && (
        <ul id={listId.current} className="hp-opt-results" role="listbox" aria-label="Instruction suggestions">
          {results.map((r, i) => (
            <li key={r}>
              <button
                type="button"
                role="option"
                aria-selected={i === active}
                className={i === active ? "is-active" : undefined}
                // Keep the focus (and the caret) in the field.
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => choose(r)}
              >
                {r}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
InstructionInput.propTypes = {
  value: PropTypes.string,
  readOnly: PropTypes.bool,
  service: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
};

/**
 * Bridge's "Search Template" (FetchPlan.js) — on the Exercise / Medical /
 * Meditation tabs of an editable version: find a section template by name and
 * load its sections. The host decides what loading means (and confirms first
 * when it replaces sections).
 */
const TemplateSearch = ({ categoryId, inert, service, onPick }) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const timer = useRef(null);
  const seq = useRef(0);
  const mounted = useRef(true);
  const boxRef = useRef(null);
  useEffect(
    () => () => {
      mounted.current = false;
      if (timer.current) clearTimeout(timer.current);
    },
    []
  );
  useCloseOnOutsidePress(boxRef, open, setOpen);
  // Another tab, or the screen locking: nothing in flight may land.
  useEffect(() => {
    seq.current += 1;
    if (timer.current) clearTimeout(timer.current);
    setQuery("");
    setResults([]);
    setOpen(false);
    setBusy(false);
  }, [categoryId]);
  useEffect(() => {
    if (!inert) return;
    seq.current += 1;
    if (timer.current) clearTimeout(timer.current);
    setOpen(false);
    setBusy(false);
  }, [inert]);

  const search = (q) => {
    setQuery(q);
    const n = ++seq.current;
    if (timer.current) clearTimeout(timer.current);
    if (!q.trim()) {
      setResults([]);
      setOpen(false);
      return;
    }
    timer.current = setTimeout(() => {
      service.searchTemplates(
        categoryId,
        q.trim(),
        (res) => {
          if (!mounted.current || n !== seq.current) return;
          setResults((Array.isArray(res) ? res : []).filter((r) => r && r.id && r.name));
          setOpen(true);
        },
        () => {
          if (!mounted.current || n !== seq.current) return;
          setResults([]);
          setOpen(true);
        }
      );
    }, 300);
  };

  const pick = (tpl) => {
    const n = ++seq.current;
    setBusy(true);
    setOpen(false);
    service.getTemplate(
      tpl.id,
      (res) => {
        if (!mounted.current || n !== seq.current) return;
        setBusy(false);
        if (!res || res.data == null) {
          EventService.emit("showError", { message: "That template is empty." });
          return;
        }
        setQuery("");
        setResults([]);
        onPick({ ...res, name: res.name || tpl.name });
      },
      (err) => {
        if (!mounted.current || n !== seq.current) return;
        setBusy(false);
        EventService.emit("showError", {
          message: `Couldn't load that template. ${(err && err.message) || ""}`.trim(),
        });
      }
    );
  };

  return (
    <div className="hp-tpl">
      {/* No "Fetch plan" label over the box (product, 2026-10-12): its
          placeholder already says what it does. */}
      <div className="hp-opt-search" ref={boxRef}>
        <input
          className="hp-input"
          placeholder="Search template"
          aria-label="Search template"
          value={query}
          readOnly={inert}
          onChange={(e) => search(e.target.value)}
          onFocus={() => query.trim() && setOpen(true)}
          onKeyDown={(e) => e.key === "Escape" && setOpen(false)}
        />
        {busy && <span className="hp-opt-busy">Loading…</span>}
        {open && !inert && (
          <ul className="hp-opt-results" role="listbox" aria-label="Templates">
            {results.length === 0 ? (
              <li className="hp-note">No template matches that.</li>
            ) : (
              results.map((r) => (
                <li key={r.id}>
                  <button type="button" disabled={busy} onClick={() => pick(r)}>
                    {r.name}
                  </button>
                </li>
              ))
            )}
          </ul>
        )}
      </div>
    </div>
  );
};
TemplateSearch.propTypes = {
  categoryId: PropTypes.string,
  inert: PropTypes.bool,
  service: PropTypes.object.isRequired,
  onPick: PropTypes.func.isRequired,
};

const AddOption = ({ itemId, disabled, inert, onAdd, service }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);
  const timer = useRef(null);
  const mounted = useRef(true);
  // Bumped when the picker goes inert: a detail answer from before is dropped.
  // A pick only READS it, so two picks in a row both land.
  const pickSeq = useRef(0);
  // Bumped on every new input (and when the picker goes inert or adds): only
  // the answer to the latest search may fill the list — never a late or
  // out-of-order one.
  const searchSeq = useRef(0);
  const boxRef = useRef(null);
  useEffect(() => () => {
    mounted.current = false;
    if (timer.current) clearTimeout(timer.current);
  }, []);
  useCloseOnOutsidePress(boxRef, open, setOpen);
  // Going inert (the host started generating) closes the picker and cancels a
  // search or a pick still in flight — its answer would land on a plan the
  // generate is about to replace. It also keeps the autoFocus search box from
  // remounting (and stealing focus) when the lock lifts.
  useEffect(() => {
    if (!inert) return;
    if (timer.current) clearTimeout(timer.current);
    pickSeq.current += 1;
    searchSeq.current += 1;
    setBusy(false);
    setOpen(false);
    setQuery("");
    setResults([]);
  }, [inert]);

  const search = (q) => {
    setQuery(q);
    const seq = ++searchSeq.current;
    if (timer.current) clearTimeout(timer.current);
    if (!q.trim()) {
      setResults([]);
      return;
    }
    timer.current = setTimeout(() => {
      service.searchOptions(
        itemId,
        q.trim(),
        (res) => {
          if (!mounted.current || seq !== searchSeq.current) return;
          setResults(optionResults(res));
        },
        () => {}
      );
    }, 300);
  };

  const pick = (opt) => {
    const seq = pickSeq.current;
    setBusy(true);
    service.getOptionDetail(
      opt.id || opt._id,
      (detail) => {
        if (!mounted.current || seq !== pickSeq.current) return;
        setBusy(false);
        setOpen(false);
        searchSeq.current += 1;
        setQuery("");
        setResults([]);
        onAdd({
          name: (detail && detail.name) || opt.name,
          description: (detail && detail.description) || "",
          images: (detail && detail.images) || [],
          recipes: (detail && detail.recipes) || [],
        });
      },
      () => {
        if (!mounted.current || seq !== pickSeq.current) return;
        setBusy(false);
        EventService.emit("showError", { message: "Couldn't load that option — try again." });
      }
    );
  };

  if (disabled) return null;
  if (!open || inert) {
    return (
      <button type="button" className="hp-add-option" disabled={inert} onClick={() => setOpen(true)}>
        + Add option
      </button>
    );
  }
  return (
    <div className="hp-opt-search" ref={boxRef}>
      <input
        className="hp-input"
        placeholder="Search options…"
        value={query}
        autoFocus
        onChange={(e) => search(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Escape") setOpen(false);
        }}
      />
      {busy && <span className="hp-opt-busy">Adding…</span>}
      {results.length > 0 && (
        <ul className="hp-opt-results" role="listbox">
          {results.map((r) => (
            <li key={r.id || r._id || r.name}>
              {/* One add at a time: a double click must not add it twice. */}
              <button type="button" disabled={busy} onClick={() => pick(r)}>
                {r.name}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
AddOption.propTypes = {
  itemId: PropTypes.any,
  disabled: PropTypes.bool,
  inert: PropTypes.bool,
  onAdd: PropTypes.func.isRequired,
  service: PropTypes.object.isRequired,
};

const HealthPlan = ({ match, history }) => {
  const appointmentId = Number(match.params.appointmentId);
  const appointmentService = useRef(new AppointmentService()).current;
  const patientService = useRef(new PatientService()).current;
  const service = useRef(new HealthPlanService()).current;
  const auth = useRef(new AuthenticationService()).current;
  const mounted = useRef(true);
  useEffect(() => () => {
    mounted.current = false;
  }, []);

  const [patientId, setPatientId] = useState(null);
  const [patient, setPatient] = useState(null); // full record — notifications need phone/clientCode
  const patientName = patient
    ? [patient.firstName, patient.lastName].filter(Boolean).join(" ").trim()
    : "";
  const [categories, setCategories] = useState([]);
  const [catItems, setCatItems] = useState({}); // catId -> master items
  const [plan, setPlan] = useState(null); // the version being viewed/edited
  const [latestId, setLatestId] = useState(null);
  // The SAVED latest version's level — generating on anything below Version 3
  // makes the server start a new Version 3 from the generated Nutrition alone.
  const [latestLevel, setLatestLevel] = useState(0);
  // The plan data as it last came from the server (or a fresh local start).
  // Every edit goes through patchData, which builds a new `data` object, so
  // `plan.data !== savedDataRef.current` means there is unsaved work.
  const savedDataRef = useRef(null);
  // Bumped whenever the latest is re-read in place: part of the uncontrolled
  // editors' keys, so they re-seed from the reloaded data instead of keeping
  // the text they mounted with (a generate can keep the id and the level).
  const [planRev, setPlanRev] = useState(0);
  // Which saved latest the screen last loaded — a failed generate compares it
  // with the server's latest to tell "nothing written" from "written, then failed".
  const latestStampRef = useRef(null);
  const stampOf = (hp) => (hp ? [hp.id || "", hp.current_level || "", hp.updatedAt || ""].join("|") : "");
  const [planList, setPlanList] = useState([]);
  const [units, setUnits] = useState([]);
  // Ingredient master list for a recipe's "+ Add ingredient" (bridge fetches
  // the whole list once; failure just means no add affordance, never a blocker).
  const [ingredientMasters, setIngredientMasters] = useState([]);
  const [activeCatId, setActiveCatId] = useState(null);
  // The tab as of the latest render — read by async callbacks, whose own
  // closure holds the tab from when they were created.
  const activeCatIdRef = useRef(activeCatId);
  activeCatIdRef.current = activeCatId;
  const [nutritionView, setNutritionView] = useState("items"); // items | guidelines
  // The Nutrition tab's Food list card — closed to begin with (legacy), and
  // kept as it was left across tab switches.
  const [foodListOpen, setFoodListOpen] = useState(false);
  const [versionsOpen, setVersionsOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [saving, setSaving] = useState(false);
  const [legacy, setLegacy] = useState(false); // un-migrated (v3) patient
  const [generating, setGenerating] = useState(false);
  const [guidelineMasters, setGuidelineMasters] = useState({}); // catId -> masters
  const [foodList, setFoodList] = useState(null); // /v1/foodlist payload
  const [foodListState, setFoodListState] = useState("idle"); // idle|loading|loaded|error
  const mainRef = useRef(null);

  const employee = useMemo(() => {
    const u = auth.getUserProfile() || {};
    const e = u.employeeProfile || {};
    /* The doctor login returns employeeProfile: null, so without these
       fallbacks every plan would be stamped employeeId 0 / "Orbit user". u.id is
       the auth id — the same value the token's uid claim carries. */
    const d = u.doctorProfile || {};
    return {
      /* The AUTH id first. hp-automation-service reads this id as a user id
         everywhere — the Generate path's `:userId`, createdBy / updatedBy /
         publishedById — and resolves it with GET /employees/user/:userId, which
         looks up the auth row. employeeProfile.id is the employee TABLE's own key
         (employee_login.go returns `ID: auth.ID, EmployeeProfile: employee`), so
         sending it first made Generate answer "invalid user" for employees, or
         stamp the plan with whoever owned that auth id. Bridge's old health-plan
         screen sent user.id too (new-bridge/.../health-plan/newIndex.js). */
      id: u.id || e.id || d.id || 0,
      name:
        [e.firstName, e.lastName].filter(Boolean).join(" ").trim() ||
        d.name ||
        "Orbit user",
    };
  }, [auth]);

  const showError = useCallback(
    (message) => EventService.emit("showError", { message }),
    []
  );
  const showSuccess = useCallback(
    (message) => EventService.emit("showError", { message, tone: "success" }),
    []
  );

  /* ---------- load: appointment -> patient -> categories -> plan ---------- */
  useEffect(() => {
    if (!appointmentId) {
      setLoadError("That appointment link looks wrong.");
      setLoading(false);
      return;
    }
    appointmentService.fetchAppointmentById(
      appointmentId,
      (res) => {
        if (!mounted.current) return;
        const found = res && res.appointments && res.appointments[0];
        if (!found || !found.patientID) {
          setLoadError("We couldn't find that appointment.");
          setLoading(false);
          return;
        }
        /* WHOSE PLAN THIS IS. On a family booking the appointment — and so the
           plan — is for `bookedFor`, the member; `patientID` is only the
           account holder who booked it. This screen used to take patientID
           unconditionally, and that one id drives the READ (fetchLatest) and
           every WRITE (create/update, the plan body's own patientId via
           newPlanSkeleton, generatePdf, the publish event) — so a plan opened
           from a child's appointment loaded the PARENT's plan, and saving
           versioned the child's clinical content onto the parent's record while
           the child's plan was never written at all.

           The guard above stays on `found.patientID`, deliberately: patientID
           is always present, and it is bookedFor that is conditionally 0. */
        /* ...unless phr's booking-for mapping (the appointment's `updatedBookingFor`,
           "Update booking for") points it at another member — then THAT member's plan. */
        const pid =
          overrideMemberId(found) ||
          (found.bookedFor && found.bookedFor !== 0 ? found.bookedFor : found.patientID);
        setPatientId(pid);
        patientService.fetchPatient(
          pid,
          (p) => {
            if (!mounted.current) return;
            if (p && p.id) setPatient(p);
          },
          () => {}
        );
      },
      (err) => {
        if (!mounted.current) return;
        setLoadError(`We couldn't load that appointment. ${(err && err.message) || ""}`);
        setLoading(false);
      }
    );
  }, [appointmentId, appointmentService, patientService]);

  useEffect(() => {
    if (!patientId) return;
    // Bridge's migration gate: a falsy answer means the patient is still on the
    // legacy (v3) plan, which phr doesn't render. An ERROR deliberately does
    // NOT block — a network blip shouldn't dead-end the tab, and an
    // un-migrated patient's /v4 latest simply comes back empty.
    service.checkMigration(
      patientId,
      (res) => {
        if (!mounted.current) return;
        if (!res) setLegacy(true);
      },
      () => {}
    );
    service.getCategories(
      (cats) => {
        if (!mounted.current) return;
        const list = Array.isArray(cats) ? cats : [];
        setCategories(list);
        // Default to Nutrition when the server has it, else the first tab.
        const nutrition = list.find((c) => /nutrition/i.test(c.name));
        setActiveCatId((nutrition || list[0] || {}).id || null);

        service.fetchLatest(
          patientId,
          (hp) => {
            if (!mounted.current) return;
            if (hp && hp.data) {
              const loaded = normalizePlan(hp, list);
              savedDataRef.current = loaded.data;
              latestStampRef.current = stampOf(hp);
              setPlan(loaded);
              setLatestId(hp.id || null);
              setLatestLevel(levelNumber(hp.current_level));
            } else {
              setPlan(null);
            }
            setLoading(false);
          },
          () => {
            // No plan yet is a normal state, not an error page.
            if (!mounted.current) return;
            setPlan(null);
            setLoading(false);
          }
        );

        service.fetchList(
          patientId,
          (res) => {
            if (!mounted.current) return;
            setPlanList(Array.isArray(res) ? res : []);
          },
          () => {}
        );
      },
      (err) => {
        if (!mounted.current) return;
        setLoadError(`Couldn't load health-plan categories. ${(err && err.message) || ""}`);
        setLoading(false);
      }
    );
    service.getIngredientUnits(
      (res) => {
        if (!mounted.current) return;
        setUnits(Array.isArray(res) ? res : []);
      },
      () => {}
    );
  }, [patientId, service]);

  // Master items for the active category, cached per category.
  useEffect(() => {
    if (!activeCatId || catItems[activeCatId]) return;
    service.getItemsByCategory(
      activeCatId,
      (res) => {
        if (!mounted.current) return;
        setCatItems((prev) => ({ ...prev, [activeCatId]: Array.isArray(res) ? res : [] }));
      },
      () => {}
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCatId, service]);

  // Master guidelines for the "+ Add guideline" picker, cached per category.
  useEffect(() => {
    if (!activeCatId || nutritionView !== "guidelines" || guidelineMasters[activeCatId]) return;
    service.getGuidelinesByCategory(
      activeCatId,
      (res) => {
        if (!mounted.current) return;
        setGuidelineMasters((prev) => ({
          ...prev,
          [activeCatId]: Array.isArray(res) ? res : [],
        }));
      },
      () => {}
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCatId, nutritionView, service]);

  /* ---------- derived ---------- */
  const activeCat = categories.find((c) => c.id === activeCatId) || {};
  const isNutrition = /nutrition/i.test(activeCat.name || "");
  // Bridge offers its section templates ("Fetch plan") on these three tabs.
  const isTemplateTab = /^(exercise|medical|meditation)$/i.test(String(activeCat.name || "").trim());
  const version = plan ? levelNumber(plan.current_level) : null;
  const isViewingOld = Boolean(plan && plan.id && latestId && plan.id !== latestId);
  const readOnly = Boolean((plan && plan.published) || isViewingOld);
  // Nothing on screen may change while a generate is in flight: its reload
  // replaces the plan, so an edit typed meanwhile would silently vanish, and a
  // save would race the server's own write. `readOnly` still decides what the
  // version OFFERS (the Generate buttons stay, showing "Generating…").
  // Saving is held the same way, through the reload that follows it.
  const busy = generating || saving;
  const locked = readOnly || busy;

  // The patient's food list, fetched once the Nutrition tab opens: its Food list
  // card lists it, and its option cards are flagged against it (old bridge's
  // "Not Allowed" / "Limited" tags). Re-fetched after Generate, which rebuilds it.
  useEffect(() => {
    if (!isNutrition || foodListState !== "idle" || !patientId) return;
    setFoodListState("loading");
    service.getFoodList(
      patientId,
      "all",
      (res) => {
        if (!mounted.current) return;
        setFoodList(res || null);
        setFoodListState("loaded");
      },
      (err) => {
        if (!mounted.current) return;
        // A patient with no food list yet answers 404 with a null body — not a
        // failure, just nothing to list or flag.
        if (err == null) {
          setFoodList(null);
          setFoodListState("loaded");
          return;
        }
        setFoodListState("error");
      }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNutrition, foodListState, patientId, service]);
  const foodIndex = useMemo(() => foodListIndex(foodList), [foodList]);

  // The patient's SAVED daily schedule (the Daily schedule v2 section), for
  // bridge's hint under a section whose name is one of its activities. The
  // schedule is one document per day since ZY-10330, so this is the CURRENT
  // day's (latestSchedule); a day nobody filled answers `filled: false`.
  const [routine, setRoutine] = useState([]);
  useEffect(() => {
    if (!patientId) return undefined;
    let alive = true;
    const read = () =>
      readLatestSchedule(
        patientId,
        (res) => {
          if (!alive) return;
          setRoutine(res && res.filled ? (res.entries || []).filter((e) => e && !e.deleted) : []);
        },
        () => {}
      );
    read();
    // The schedule is edited on another screen, usually in another tab.
    const onVisible = () => {
      if (document.visibilityState === "visible") read();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      alive = false;
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [patientId]);
  const routineTime = (name) => {
    const key = normalizeActivity(name).toLowerCase();
    const hit = key && routine.find((e) => normalizeActivity(e.activity).toLowerCase() === key);
    return hit ? formatTimeAmPm(hit.timeMinutes).toUpperCase() : "";
  };

  // The ingredient master list is only needed by "+ Add ingredient", so fetch it
  // once and ONLY on an editable plan — it's the whole catalogue, and a
  // published/old version can never use it.
  useEffect(() => {
    if (readOnly || !plan || ingredientMasters.length) return;
    service.getIngredients(
      (res) => {
        if (!mounted.current) return;
        setIngredientMasters(Array.isArray(res) ? res : (res && res.ingredients) || []);
      },
      () => {}
    );
    // ingredientMasters is read as a "already fetched?" guard, not a trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readOnly, plan, service]);
  const catData = (plan && plan.data && plan.data[activeCatId]) || { items: [], guidelines: [] };
  const sections = catData.items || [];
  const guidelines = catData.guidelines || [];
  const masterItems = catItems[activeCatId] || [];
  /* Each listed version's own date and author — the list carries neither (see
     toVersionRows). Read once the history panel opens, four at a time, each
     version once per list load; a save reloads the list, and so re-reads. */
  const [versionDetails, setVersionDetails] = useState({});
  const askedVersionsRef = useRef(new Set());
  useEffect(() => {
    askedVersionsRef.current = new Set();
    setVersionDetails({});
  }, [planList]);
  useEffect(() => {
    if (!versionsOpen) return undefined;
    const queue = planList.map((p) => p && p.id).filter((id) => id && !askedVersionsRef.current.has(id));
    queue.forEach((id) => askedVersionsRef.current.add(id));
    let live = true;
    const next = () => {
      const id = live && queue.shift();
      if (!id) return;
      service.fetchById(
        patientId,
        id,
        (hp) => {
          if (mounted.current && hp) {
            const { createdAt, updatedAt, updatedByName, published, publishedBy } = hp;
            setVersionDetails((d) => ({ ...d, [id]: { createdAt, updatedAt, updatedByName, published, publishedBy } }));
          }
          next();
        },
        () => next()
      );
    };
    for (let i = 0; i < 4; i += 1) next();
    return () => {
      live = false;
      // Never sent: ask again the next time the panel opens.
      queue.forEach((id) => askedVersionsRef.current.delete(id));
    };
  }, [versionsOpen, planList, patientId, service]);
  const versionRows = useMemo(
    () => toVersionRows(planList, plan && plan.id === latestId ? plan : null, versionDetails),
    [planList, plan, latestId, versionDetails]
  );

  /* ---------- mutators (all no-op when locked: read-only, or generating) ---------- */
  const patchData = useCallback(
    (fn) => {
      setPlan((prev) => {
        if (!prev) return prev;
        const cat = prev.data[activeCatId] || { items: [], guidelines: [] };
        const nextCat = fn({ items: [...(cat.items || [])], guidelines: [...(cat.guidelines || [])] });
        return { ...prev, data: { ...prev.data, [activeCatId]: nextCat } };
      });
    },
    [activeCatId]
  );

  const addItem = (master) => {
    if (locked) return;
    if (sections.some((s) => s.itemId === master.id)) {
      showError("Item already added");
      return;
    }
    patchData((cat) => ({
      ...cat,
      items: cat.items.concat({
        itemId: master.id,
        name: master.name,
        description: master.description || "",
        image: master.image || "",
        time: "",
        options: [],
      }),
    }));
  };
  const removeItem = (idx) =>
    !locked && patchData((cat) => ({ ...cat, items: cat.items.filter((_, i) => i !== idx) }));
  const patchItem = (idx, patch) =>
    !locked &&
    patchData((cat) => ({
      ...cat,
      items: cat.items.map((it, i) => (i === idx ? { ...it, ...patch } : it)),
    }));
  // By the section's itemId when that names exactly one section: an option picked
  // asynchronously can arrive after a reorder or removal has moved sections off
  // their old index. Migrated plans can give several unmapped sections the same
  // (zero) itemId — then only the index can tell them apart.
  const addOption = (idx, itemId, option) => {
    if (locked) return;
    // Bridge adds an option once per section (by name) — a second pick of the
    // same option is dropped. Said here; enforced in the update itself.
    const shown = sections.filter((it) => it.itemId === itemId);
    const target = itemId != null && shown.length === 1 ? shown[0] : sections[idx];
    if (target && (target.options || []).some((o) => o.name === option.name)) {
      showError(`"${option.name}" is already in ${target.name}.`);
      return;
    }
    patchData((cat) => {
      const byId = itemId != null && cat.items.filter((it) => it.itemId === itemId).length === 1;
      return {
        ...cat,
        items: cat.items.map((it, i) =>
          (byId ? it.itemId === itemId : i === idx) &&
          !(it.options || []).some((o) => o.name === option.name)
            ? { ...it, options: (it.options || []).concat(option) }
            : it
        ),
      };
    });
  };
  const removeOption = (idx, oIdx) =>
    !locked &&
    patchData((cat) => ({
      ...cat,
      items: cat.items.map((it, i) =>
        i === idx ? { ...it, options: (it.options || []).filter((_, o) => o !== oIdx) } : it
      ),
    }));
  const patchOption = (idx, oIdx, patch) =>
    !locked &&
    patchData((cat) => ({
      ...cat,
      items: cat.items.map((it, i) =>
        i === idx
          ? {
              ...it,
              options: (it.options || []).map((o, oi) => (oi === oIdx ? { ...o, ...patch } : o)),
            }
          : it
      ),
    }));
  /**
   * Recipe + ingredient edits. Recipes hang off an option, so every write walks
   * items → options → recipes immutably (same shape as patchOption).
   * `mut(recipe)` returns the replacement recipe.
   */
  const patchRecipeAt = (idx, oIdx, rIdx, mut) =>
    !locked &&
    patchData((cat) => ({
      ...cat,
      items: cat.items.map((it, i) =>
        i === idx
          ? {
              ...it,
              options: (it.options || []).map((o, oi) =>
                oi === oIdx
                  ? {
                      ...o,
                      recipes: (o.recipes || []).map((r, ri) => (ri === rIdx ? mut(r) : r)),
                    }
                  : o
              ),
            }
          : it
      ),
    }));

  const patchRecipe = (idx, oIdx, rIdx, patch) =>
    patchRecipeAt(idx, oIdx, rIdx, (r) => ({ ...r, ...patch }));

  const patchIngredient = (idx, oIdx, rIdx, ingIdx, patch) =>
    patchRecipeAt(idx, oIdx, rIdx, (r) => ({
      ...r,
      ingredients: (r.ingredients || []).map((g, gi) =>
        gi === ingIdx ? { ...g, ...patch } : g
      ),
    }));

  const addIngredient = (idx, oIdx, rIdx, ingredient) =>
    patchRecipeAt(idx, oIdx, rIdx, (r) => ({
      ...r,
      ingredients: (r.ingredients || []).concat(ingredient),
    }));

  const removeIngredient = (idx, oIdx, rIdx, ingIdx) =>
    patchRecipeAt(idx, oIdx, rIdx, (r) => ({
      ...r,
      ingredients: (r.ingredients || []).filter((g, gi) => gi !== ingIdx),
    }));

  const addGuidelineFromMaster = (master) =>
    !locked &&
    patchData((cat) => ({
      ...cat,
      // Bridge unshifts the master itself, so its ranges come along.
      guidelines: [
        {
          name: master.name,
          minRange: master.minRange == null ? "" : String(master.minRange),
          maxRange: master.maxRange == null ? "" : String(master.maxRange),
          unit: master.unit || "",
          description: master.description || "",
          image: master.image || "",
        },
      ].concat(cat.guidelines),
    }));
  /* ---------- drag-to-reorder (sections, guidelines) ---------- */
  // What's being dragged; a ref so dragover/drop don't re-render per pixel.
  const dragRef = useRef(null);
  const reorderSections = (from, to) =>
    !locked &&
    from !== to &&
    patchData((cat) => ({ ...cat, items: moveInArray(cat.items, from, to) }));
  const reorderGuidelines = (from, to) =>
    !locked &&
    from !== to &&
    patchData((cat) => ({ ...cat, guidelines: moveInArray(cat.guidelines, from, to) }));

  /** Handle props for a draggable grip. `info` identifies the source row. */
  const gripProps = (info) => ({
    draggable: true,
    onDragStart: (e) => {
      dragRef.current = info;
      e.dataTransfer.setData("text/plain", info.kind); // Firefox needs data set
      e.dataTransfer.effectAllowed = "move";
    },
    onDragEnd: () => {
      dragRef.current = null;
    },
  });

  /** Drop-target props: accepts only a matching kind. */
  const dropProps = (kind, targetIdx) => ({
    onDragOver: (e) => {
      const d = dragRef.current;
      if (d && d.kind === kind) {
        e.preventDefault(); // required, or the browser refuses the drop
        e.dataTransfer.dropEffect = "move";
      }
    },
    onDrop: (e) => {
      const d = dragRef.current;
      if (!d || d.kind !== kind) return;
      e.preventDefault();
      if (kind === "section") reorderSections(d.idx, targetIdx);
      else if (kind === "guideline") reorderGuidelines(d.idx, targetIdx);
      dragRef.current = null;
    },
  });

  const patchGuideline = (gIdx, patch) =>
    !locked &&
    patchData((cat) => ({
      ...cat,
      guidelines: cat.guidelines.map((g, i) => (i === gIdx ? { ...g, ...patch } : g)),
    }));
  const removeGuideline = (gIdx) =>
    !locked &&
    patchData((cat) => ({ ...cat, guidelines: cat.guidelines.filter((_, i) => i !== gIdx) }));

  /* ---------- version flows ---------- */
  /** Re-read the saved latest. `onDone(applied)` runs once it has been applied,
   *  or the read failed / found nothing — on every way out (a caller holding
   *  the screen locked is waiting on it, so the read is a single attempt that
   *  always answers). `reseed` re-mounts the rich-text editors from the reloaded
   *  data: only a generate needs that (it can keep the id and level while
   *  replacing the text); a save's reload must not yank the editors. */
  const reloadLatest = useCallback((onDone, { reseed = false } = {}) => {
    const waiting = typeof onDone === "function";
    const done = (applied) => {
      if (mounted.current && waiting) onDone(applied);
    };
    if (!patientId) {
      done(false);
      return;
    }
    const read = waiting ? service.fetchLatestOnce.bind(service) : service.fetchLatest.bind(service);
    read(
      patientId,
      (hp) => {
        if (!mounted.current) return;
        if (hp && hp.data) {
          const loaded = normalizePlan(hp, categories);
          savedDataRef.current = loaded.data;
          latestStampRef.current = stampOf(hp);
          setPlan(loaded);
          setLatestId(hp.id || null);
          setLatestLevel(levelNumber(hp.current_level));
          if (reseed) setPlanRev((r) => r + 1);
          done(true);
          return;
        }
        done(false);
      },
      () => done(false)
    );
    service.fetchList(
      patientId,
      (res) => {
        if (!mounted.current) return;
        setPlanList(Array.isArray(res) ? res : []);
      },
      () => {}
    );
  }, [patientId, categories, service]);

  const openVersion = (id) => {
    setVersionsOpen(false);
    if (!id || id === (plan && plan.id)) return;
    if (id === latestId) {
      reloadLatest();
      return;
    }
    service.fetchById(
      patientId,
      id,
      (hp) => {
        if (!mounted.current) return;
        if (hp && hp.data) {
          const loaded = normalizePlan(hp, categories);
          savedDataRef.current = loaded.data;
          setPlan(loaded);
        }
      },
      (err) => showError(`Couldn't open that version. ${(err && err.message) || ""}`)
    );
  };

  /** Bridge's addNewLevel ("Add New"): offered on a published latest, and on a
   *  draft below Version 3. Below 3 the next version is an EMPTY Version 3;
   *  from 3 up it is N+1 carrying the data forward (nextVersionPlan). */
  const startNewVersion = () => {
    if (!plan || busy) return;
    if (!plan.published && plan.data !== savedDataRef.current) {
      setConfirmReq({
        title: "Start Version 3?",
        body: "Unsaved changes on this version will be lost.",
        confirmLabel: "Start Version 3",
        action: "new-version",
      });
      return;
    }
    runNewVersion();
  };
  const runNewVersion = () => {
    if (!plan) return;
    const next = nextVersionPlan(plan, categories);
    // A fresh (empty) version has nothing unsaved yet; a carried one is the
    // saved data itself.
    if (next.data !== plan.data) savedDataRef.current = next.data;
    setPlan(next);
  };

  /** Bridge's setPlanToView: a picked template's sections replace those of
   *  every category it covers — asked first when that loses sections. */
  const applyTemplate = (tpl) => {
    if (!plan || locked) return;
    const covered = categories.filter((c) => templateSections(tpl, c.id) !== null);
    if (!covered.length) {
      showError(`"${tpl.name}" has no sections for this plan.`);
      return;
    }
    const losing = covered.filter((c) => ((plan.data[c.id] || {}).items || []).length > 0);
    if (losing.length) {
      setConfirmReq({
        title: `Use template "${tpl.name}"?`,
        body: `Its sections replace the ${losing.map((c) => c.name).join(", ")} sections on this version.`,
        confirmLabel: "Use template",
        action: "template",
        template: tpl,
      });
      return;
    }
    runTemplate(tpl);
  };
  const runTemplate = (tpl) => {
    if (locked) return;
    setPlan((prev) => {
      if (!prev) return prev;
      const data = { ...prev.data };
      categories.forEach((c) => {
        const items = templateSections(tpl, c.id);
        if (items) data[c.id] = { ...(data[c.id] || { guidelines: [] }), items };
      });
      return { ...prev, data };
    });
    showSuccess(`Template "${tpl.name}" loaded — save to keep it.`);
  };

  const startFirstPlan = () => {
    const skeleton = newPlanSkeleton(patientId, categories);
    savedDataRef.current = skeleton.data;
    setPlan(skeleton);
  };

  /* ---------- publish notifications (bridge's growthEvents, prod-gated) ---------- */
  const environmentService = useRef(new EnvironmentService()).current;

  /* Bridge runs CleverTap from app start. phr starts bridge's A/B account only
     where its event can fire — a PRODUCTION health plan of a 02XXAB patient —
     and as soon as that screen opens: the SDK holds events until its server
     has handed it a session, so it must be up well before a publish. */
  useEffect(() => {
    if (patient && patient.clientCode === "02XXAB" && environmentService.isProduction()) cleverTapAB();
  }, [patient, environmentService]);

  /**
   * Bridge fires the WhatsApp event only once the PDF exists: poll the status
   * endpoint every second (capped) until { pdfURL } shows up, then send
   * HP_PUBLISHED (HP_3_PUBLISHED for level 3). All fire-and-forget — a
   * notification hiccup must never look like a failed publish.
   */
  const pollPdfAndNotify = useCallback(
    (hpId, level) => {
      if (!environmentService.isProduction() || !patient) return;
      let tries = 0;
      const intervalId = setInterval(() => {
        tries += 1;
        if (tries > 60 || !mounted.current) {
          clearInterval(intervalId);
          return;
        }
        service.getPdfStatus(
          patientId,
          hpId,
          (res) => {
            if (!res || !res.pdfURL) return; // keep polling
            clearInterval(intervalId);
            service.whatsappEvent(
              {
                userId: String(patientId),
                phoneNumber: String(patient.phoneno),
                countryCode: String(patient.countryCode),
                event: level === 3 ? "HP_3_PUBLISHED" : "HP_PUBLISHED",
                traits: {},
              },
              () => {},
              () => {}
            );
            // Bridge's CRM disposition — its wording, typo and all, since the
            // CRM matches on it.
            service.fetchDispositionDetails(
              patient.phoneno,
              (details) => {
                if (!details) return;
                service.createDisposition(`Sucessfully Published ${level}`, details, () => {}, () => {});
              },
              () => {}
            );
            // Bridge's 02XXAB A/B event, on bridge's CleverTap A/B account
            // (started when this screen opened — see above).
            if (patient.clientCode === "02XXAB") {
              service.memberMapping(
                patient.id,
                (mapping) => {
                  if (!mapping || !mapping.memberId || !String(mapping.memberId).length) return;
                  const clevertap = cleverTapAB();
                  clevertap.onUserLogin.push({ Site: { Identity: mapping.memberId } });
                  clevertap.event.push("ZYLA_HealthPlan", { Identity: mapping.memberId, url: res.pdfURL });
                  clevertap.logout();
                },
                () => {}
              );
            }
          },
          () => clearInterval(intervalId)
        );
      }, 1000);
    },
    [environmentService, patient, patientId, service]
  );

  /** 03XXAZ referral-feedback SMS: doctor name → short URL → templated SMS. */
  const sendReferralFeedback = useCallback(() => {
    if (!environmentService.isProduction()) return;
    if (!patient || patient.clientCode !== "03XXAZ" || !patient.referralCode) return;
    service.searchDoctorCode(
      patient.referralCode,
      (doc) => {
        if (!doc || !doc.name) return;
        const feedback =
          `${HP_FEEDBACK_LINK}&customer_email=${patient.email || ""}` +
          `&customer_first_name=${patient.firstName || ""}&company_name=${patient.phoneno}`;
        service.whatsappShort(
          { originalURL: feedback },
          (short) => {
            if (!short || !short.secureShortURL) return;
            // Bridge strips a leading "Dr"/"DR" so the template's own "Dr" isn't doubled.
            const name = String(doc.name).replace(/^\s*dr\.?\s*/i, "").trim() || doc.name;
            service.sendSms(
              {
                body: SMS_BODY.format(name, short.secureShortURL),
                entityid: "1301158167847151139",
                phoneNo: String(patient.phoneno),
                senderId: "ZYLAHE",
                templateid: "1107167112299439727",
              },
              () => {},
              () => {}
            );
          },
          () => {}
        );
      },
      () => {}
    );
  }, [environmentService, patient, service]);

  /* The editor's confirmation popup (HpConfirmDialog) — what it asks, and
     what a confirm does: "generate", "publish-medical" / "save-medical" (the
     empty-Medical warning — it leads on to the publish confirm, or saves the
     draft), "publish", "new-version" or "template". Run through
     the handlers of the render that shows it, so a confirm acts on the current
     plan. */
  const [confirmReq, setConfirmReq] = useState(null);
  const cancelConfirm = () => setConfirmReq(null);
  const acceptConfirm = () => {
    const req = confirmReq;
    setConfirmReq(null);
    if (!req) return;
    if (req.action === "generate") runGenerate();
    else if (req.action === "publish-medical") askPublish();
    else if (req.action === "save-medical") runSave(false);
    else if (req.action === "publish") runSave(true);
    else if (req.action === "new-version") runNewVersion();
    else if (req.action === "template") runTemplate(req.template);
  };
  const nutritionCategory = () => categories.find((c) => /nutrition/i.test(c.name || ""));

  /** Bridge's "Generate Healthplan" — server builds the nutrition plan (its
   *  options arrive with auto:true), then we re-pull the latest. Offered in the
   *  version bar on every tab and on an empty Nutrition tab. */
  const generatePlan = () => {
    if (generating || saving) return;
    // What the server does (hp-automation BuildNutritionHealthPlan): below
    // Version 3 it starts a NEW Version 3 holding only the generated Nutrition;
    // from Version 3 up it swaps the Nutrition tab (sections and guidelines) of
    // the SAVED latest, as a new version when that one is published. Either way
    // the reload that follows replaces whatever is on screen. Ask whenever that
    // loses something.
    const nutritionCat = nutritionCategory();
    const data = (plan && plan.data) || {};
    const hasContent = (catId) => {
      const cat = data[catId];
      return !!cat && ((cat.items || []).length > 0 || (cat.guidelines || []).length > 0);
    };
    const replacesNutrition = !!nutritionCat && hasContent(nutritionCat.id);
    const dropsOtherTabs =
      latestLevel < 3 && Object.keys(data).some((id) => (!nutritionCat || id !== nutritionCat.id) && hasContent(id));
    const unsaved = !!plan && plan.data !== savedDataRef.current;
    if (replacesNutrition || dropsOtherTabs || unsaved) {
      const lines = [];
      if (dropsOtherTabs) {
        lines.push(
          "It starts a new Version 3 with only the generated Nutrition plan. The other tabs of this version are not carried into it."
        );
      } else if (replacesNutrition) {
        lines.push("The generated Nutrition plan replaces this version's Nutrition sections and guidelines.");
      }
      if (unsaved) lines.push("Unsaved changes are lost — save first to keep them.");
      setConfirmReq({
        title: "Generate the health plan?",
        body: lines,
        confirmLabel: "Generate",
        action: "generate",
      });
      return;
    }
    runGenerate();
  };

  /** The generate itself, once nothing needs asking (or it was confirmed). */
  const runGenerate = () => {
    if (generating || saving) return;
    const nutritionCat = nutritionCategory();
    setGenerating(true);
    service.generateNutrition(
      patientId,
      employee.id,
      (res) => {
        if (!mounted.current) return;
        // 204 "No metadata exists to generate hp": nothing was generated. (A
        // patient without assessment data still gets a fallback plan — this is
        // the server finding no diet metadata at all.)
        if (res === undefined) {
          setGenerating(false);
          showError("Couldn't generate: the server built no plan for this patient (no diet metadata matched).");
          return;
        }
        showSuccess("Health plan generated");
        // Generating rebuilds the patient's food list too.
        setFoodList(null);
        setFoodListState("idle");
        // Show what changed: generating only ever builds the Nutrition tab.
        if (nutritionCat && activeCatIdRef.current !== nutritionCat.id) {
          setActiveCatId(nutritionCat.id);
          setNutritionView("items");
        }
        // Stay locked until the generated plan is on screen: unlocking first
        // would re-open the editors on the OLD plan for a round trip.
        reloadLatest(
          (applied) => {
            setGenerating(false);
            if (!applied) {
              showError("The health plan was generated, but it couldn't be loaded — reload the page before editing it.");
            }
          },
          { reseed: true }
        );
      },
      (err) => {
        if (!mounted.current) return;
        showError(`Couldn't generate: ${(err && err.message) || "please try again."}`);
        // The server writes the food list, then the plan, and can still fail
        // after that (or the connection can drop once it has). A refetch of the
        // food list costs nothing; the plan is reloaded only if the server's
        // latest actually changed — otherwise the screen, unsaved edits
        // included, stays as it was.
        setFoodList(null);
        setFoodListState("idle");
        // Still locked until it is known whether anything was written.
        service.fetchLatestOnce(
          patientId,
          (hp) => {
            if (!mounted.current) return;
            if (!hp || !hp.data || stampOf(hp) === latestStampRef.current) {
              setGenerating(false);
              return;
            }
            if (nutritionCat && activeCatIdRef.current !== nutritionCat.id) {
              setActiveCatId(nutritionCat.id);
              setNutritionView("items");
            }
            reloadLatest(
              (applied) => {
                setGenerating(false);
                // Only claim a reload that happened: if it didn't, the OLD plan
                // is still on screen and a save would put it back over the
                // generated one.
                showError(
                  applied
                    ? "The server saved a generated plan despite the error — the plan was reloaded."
                    : "The server saved a generated plan despite the error, but it couldn't be loaded — reload the page before editing it."
                );
              },
              { reseed: true }
            );
          },
          () => {
            if (mounted.current) setGenerating(false);
          }
        );
      }
    );
  };

  /* ---------- save / publish ---------- */
  const save = (publish) => {
    if (!plan || saving || generating) return;
    const invalid = validatePlan(plan.data);
    if (invalid) {
      showError(invalid);
      return;
    }
    // Bridge refuses to write a plan with no sections at all.
    if (!Object.keys(dataFilter(plan.data)).length) {
      showError("Health plan cannot be empty — add at least one section.");
      return;
    }
    // Bridge's medical gate — on a draft save AND a publish: warn when the
    // Medical tab is empty. A publish then — always — confirms itself.
    const medical = categories.find((c) => /medical/i.test(c.name));
    if (medical && !((plan.data[medical.id] || {}).items || []).length) {
      setConfirmReq(
        publish
          ? {
              title: "Publish without medical guidelines?",
              body: "Medical guidelines have not been added yet.",
              confirmLabel: "Publish anyway",
              action: "publish-medical",
            }
          : {
              title: "Save without medical guidelines?",
              body: "Medical guidelines have not been added yet.",
              confirmLabel: "Save anyway",
              action: "save-medical",
            }
      );
      return;
    }
    if (!publish) {
      runSave(false);
      return;
    }
    askPublish();
  };

  const askPublish = () => {
    if (!plan) return;
    setConfirmReq({
      title: `Publish Version ${levelNumber(plan.current_level)}?`,
      body: "The patient's app updates to this plan.",
      confirmLabel: "Publish",
      action: "publish",
    });
  };

  /* The PDF bridge builds after a publish. A start that fails says so, and that
     published version's bar offers "Retry PDF" (bridge's pdfStatus 5 → "Try
     again"). */
  const [pdfFailedId, setPdfFailedId] = useState(null);
  const startPdf = (hpId, level) => {
    setPdfFailedId(null);
    service.generatePdf(
      patientId,
      hpId,
      { name: employee.name, employeeId: employee.id },
      () => pollPdfAndNotify(hpId, level),
      (err) => {
        if (!mounted.current) return;
        setPdfFailedId(hpId);
        showError((err && err.message) || "Error while generating the health plan PDF.");
      }
    );
  };

  /** The save itself — a draft, or a publish that was confirmed. */
  const runSave = (publish) => {
    if (!plan || saving || generating) return;
    setSaving(true);
    const body = buildSaveBody(plan, publish, employee);
    /* The health plan ↔ appointment mapping (product, 2026-09-18): a PUBLISH
       carries the appointment this plan was opened from, and hp-automation
       stores it on that version. A draft save never does. Added here rather
       than in buildSaveBody, which is shared with bridge — bridge does this too
       since 2026-09-28, but takes its id from `?appointmentId=` on the query
       string (its record is patient-keyed) while phr takes it from the route,
       so only the RULE is shared, not the source. */
    if (publish && appointmentId > 0) body.appointmentId = appointmentId;
    const done = (res) => {
      if (!mounted.current) return;
      showSuccess(publish ? "Health plan published" : "Draft saved");
      // Bridge kicks off PDF generation right after a publish; once the PDF is
      // ready the WhatsApp publish event fires (production only), and 03XXAZ
      // patients get the referral-feedback SMS. All fire-and-forget.
      if (publish) {
        const hpId = (res && res.id) || body.id;
        if (hpId) startPdf(hpId, levelNumber(body.current_level));
        sendReferralFeedback();
      }
      // Still locked until the saved plan is back on screen: anything typed
      // during that round trip would be overwritten by the reload.
      reloadLatest((applied) => {
        setSaving(false);
        if (!applied) {
          showError(
            `${publish ? "Published" : "Saved"}, but the health plan couldn't be reloaded — reload the page before editing it.`
          );
        }
      });
    };
    const fail = (err) => {
      if (!mounted.current) return;
      setSaving(false);
      // Surface whatever the server actually said — a bare "please try again"
      // hides 400 causes (we learned this the hard way on create).
      const msg =
        (err && (err.message || err.error || err.detail)) ||
        (typeof err === "string" ? err : "") ||
        "please try again.";
      showError(`Couldn't save: ${msg}`);
    };
    if (body.id) {
      service.update(patientId, body.id, body, done, fail);
      return;
    }
    // Creating a new version: another client (e.g. bridge) may have published
    // a version while this page was open, and the server rejects a duplicate
    // level with an opaque 400. Re-check latest first — through the read that
    // always answers, since the page is locked until this save settles.
    service.fetchLatestOnce(
      patientId,
      (latest) => {
        if (!mounted.current) return;
        if (latest && levelNumber(latest.current_level) >= body.level) {
          setSaving(false);
          showError(
            `Version ${levelNumber(latest.current_level)} already exists for this patient — reopen the health plan to pick it up.`
          );
          return;
        }
        service.create(patientId, body, done, fail);
      },
      () => {
        if (!mounted.current) return;
        // Can't check (e.g. first-ever plan 404s) — let the create decide.
        service.create(patientId, body, done, fail);
      }
    );
  };

  /* ---------- nav ---------- */
  const goBack = useCallback(() => history.goBack(), [history]);
  const goToDashboard = useCallback(() => history.push("/navigo-module"), [history]);
  const crumbs = [
    { label: "My appointments", onClick: goToDashboard },
    { label: patientName || "Patient" },
  ];

  /* The page fills the viewport below whatever the host draws above it (phr:
     nothing; bridge: its fixed toolbar and the content area's padding), so the
     rail and the plan each scroll on their own and the window never does. The
     offset is measured, not assumed: bridge's toolbar is 48/56/64px by width. */
  const pageNode = useRef(null);
  const fitPageNow = useCallback(() => {
    const el = pageNode.current;
    if (!el) return;
    let below = 0;
    for (let p = el.parentElement; p && p !== document.documentElement; p = p.parentElement) {
      const cs = window.getComputedStyle(p);
      below += (parseFloat(cs.paddingBottom) || 0) + (parseFloat(cs.borderBottomWidth) || 0);
    }
    const top = el.getBoundingClientRect().top + window.pageYOffset;
    el.style.setProperty("--hp-fit", `${Math.max(0, Math.round(top + below))}px`);
  }, []);
  const fitPage = useCallback(
    (node) => {
      pageNode.current = node;
      fitPageNow();
    },
    [fitPageNow]
  );
  useEffect(() => {
    window.addEventListener("resize", fitPageNow);
    return () => window.removeEventListener("resize", fitPageNow);
  }, [fitPageNow]);

  useEffect(() => {
    if (mainRef.current) mainRef.current.scrollTop = 0;
  }, [activeCatId, nutritionView]);

  /* ---------- status line + actions ---------- */
  const statusLine = !plan
    ? ""
    : plan.published
    ? `Published by ${plan.updatedByName || "N/A"} · ${formatPlanDate(plan.updatedAt)}`
    : plan.id
    ? `Last saved by ${plan.updatedByName || "N/A"} · ${formatPlanDate(plan.updatedAt)}`
    : "Not saved yet";

  /* ---------- render ---------- */
  if (loading) {
    return (
      <div className="hp-page" ref={fitPage}>
        <TopBar crumbs={crumbs} onBack={goBack} />
        <div className="hp-message">Loading health plan…</div>
      </div>
    );
  }
  if (loadError) {
    return (
      <div className="hp-page" ref={fitPage}>
        <TopBar crumbs={crumbs} onBack={goBack} />
        <div className="hp-message">{loadError}</div>
      </div>
    );
  }
  if (legacy) {
    return (
      <div className="hp-page" ref={fitPage}>
        <TopBar crumbs={crumbs} onBack={goBack} />
        <div className="hp-message">
          <p>
            {patientName || "This patient"} is still on the legacy health plan,
            which Orbit doesn&rsquo;t edit — please manage it in Bridge.
          </p>
          <button type="button" className="hp-btn hp-btn-ghost" onClick={goBack}>
            Go back
          </button>
        </div>
      </div>
    );
  }
  if (!plan) {
    return (
      <div className="hp-page" ref={fitPage}>
        <TopBar crumbs={crumbs} onBack={goBack} />
        <div className="hp-message">
          <p>{patientName || "This patient"} has no health plan yet.</p>
          <button type="button" className="hp-btn hp-btn-primary" onClick={startFirstPlan}>
            Start a health plan
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="hp-page" ref={fitPage}>
      <TopBar crumbs={crumbs} onBack={goBack} />

      {/* ---- version bar ---- */}
      <div className="hp-topbar">
        <button type="button" className="hp-back" aria-label="Back" onClick={goBack}>
          &#8249;
        </button>
        <h1
          className="hp-version-title"
          role="button"
          tabIndex={0}
          title="Open version history"
          onClick={() => setVersionsOpen((o) => !o)}
          onKeyDown={(e) => e.key === "Enter" && setVersionsOpen((o) => !o)}
        >
          Version {version}
          {isViewingOld && <span className="hp-viewing-tag"> · viewing</span>}
        </h1>
        {/* Figma 7121:20914 — beside the version title. Offered where legacy
            offers it (NewPatientHealthPlan, Nittin 2026-09-18): the Nutrition
            tab of an editable version from Version 3 up. */}
        {!readOnly && isNutrition && version >= 3 && (
          <button
            type="button"
            className="hp-btn-generate"
            // aria-disabled, not disabled: a disabled button drops keyboard
            // focus to <body>; generatePlan already ignores repeat clicks.
            aria-disabled={busy || undefined}
            aria-busy={generating || undefined}
            title="Builds the Nutrition plan from the patient's assessment"
            onClick={generatePlan}
          >
            {generating ? "Generating…" : "Generate health plan"}
          </button>
        )}
        <div className="hp-topbar-right">
          <span className="hp-last-saved">{statusLine}</span>
          {isViewingOld ? (
            <button type="button" className="hp-btn hp-btn-ghost" onClick={() => openVersion(latestId)}>
              Back to latest
            </button>
          ) : plan.published ? (
            <>
              <button type="button" className="hp-btn hp-btn-ghost hp-btn-status" disabled title="Published versions are read-only">
                Published
              </button>
              {pdfFailedId && pdfFailedId === plan.id && (
                <button type="button" className="hp-btn hp-btn-ghost" onClick={() => startPdf(plan.id, version)}>
                  Retry PDF
                </button>
              )}
              <button type="button" className="hp-btn hp-btn-primary" onClick={startNewVersion}>
                New version
              </button>
            </>
          ) : (
            <>
              {/* Bridge's "Add New" is also there below Version 3. */}
              {version < 3 && (
                <button
                  type="button"
                  className="hp-btn hp-btn-ghost"
                  disabled={saving || generating}
                  title="Start Version 3"
                  onClick={startNewVersion}
                >
                  New version
                </button>
              )}
              <button
                type="button"
                className="hp-btn hp-btn-ghost"
                disabled={saving || generating}
                onClick={() => save(false)}
              >
                {saving ? "Saving…" : "Save draft"}
              </button>
              <button
                type="button"
                className="hp-btn hp-btn-primary"
                disabled={saving || generating}
                onClick={() => save(true)}
              >
                Publish
              </button>
            </>
          )}
        </div>
      </div>

      {/* ---- body ---- */}
      <div className="hp-body">
        <aside className="hp-sidebar">
          <button
            type="button"
            className="hp-versions-head"
            onClick={() => setVersionsOpen((o) => !o)}
            aria-expanded={versionsOpen}
          >
            Versions <Chev />
          </button>

          <div className="hp-levels">
            {categories.map((c) => {
              const isCatNutrition = /nutrition/i.test(c.name || "");
              return (
                <React.Fragment key={c.id}>
                  <button
                    type="button"
                    className={`hp-level${activeCatId === c.id ? " is-active" : ""}`}
                    onClick={() => {
                      setActiveCatId(c.id);
                      if (isCatNutrition) setNutritionView("items");
                    }}
                  >
                    {activeCatId === c.id && (
                      <span className="hp-level-accent" aria-hidden="true" />
                    )}
                    {c.name}
                  </button>
                  {/* Guidelines lives as a SUBTAB under Nutrition in this nav
                      (Nittin, 2026-07-23 — replaces the old top sub-bar). Shown
                      only while Nutrition is the active tab. The Food list
                      subtab is gone (2026-09-17): the list is a card at the top
                      of the Nutrition tab, where legacy shows it. */}
                  {isCatNutrition && activeCatId === c.id && (
                    <div className="hp-subtabs">
                      <button
                        type="button"
                        className={`hp-subtab${nutritionView === "guidelines" ? " is-active" : ""}`}
                        onClick={() => setNutritionView("guidelines")}
                      >
                        Guidelines
                      </button>
                    </div>
                  )}
                </React.Fragment>
              );
            })}
          </div>

          <div className="hp-side-divider" aria-hidden="true" />

          <nav className="hp-sections-nav">
            {masterItems.map((master) => {
              const added = sections.some((s) => s.itemId === master.id);
              return (
                // Legacy (HealthPlanItem): disabled on a read-only version; an
                // added one only says so ("Item already added").
                <button
                  key={master.id}
                  type="button"
                  className={`hp-nav-item${added ? " is-active" : ""}`}
                  title={readOnly ? "Read-only version" : added ? "Already added" : "Add this section"}
                  disabled={readOnly}
                  onClick={() => addItem(master)}
                >
                  <span className={`hp-nav-ico${added ? " is-minus" : ""}`} aria-hidden="true">
                    {added ? "−" : "+"}
                  </span>
                  {master.name}
                </button>
              );
            })}
          </nav>

          {/* Legacy's Food preferences / Daily schedule accordions, after the
              section tiles and on the Nutrition tab only — display only, read
              from the patient sections' v2 APIs; edited there. Hidden rather
              than unmounted elsewhere, so they are not re-read and keep their
              open/closed state across tab switches. */}
          <div className="hp-side-profile" hidden={!isNutrition}>
            <div className="hp-side-divider" aria-hidden="true" />
            <HealthPlanProfilePanels patientId={patientId} />
          </div>

          {versionsOpen && (
            <VersionHistoryPanel
              rows={versionRows}
              latestId={latestId}
              viewingId={plan.id}
              onOpen={openVersion}
              onClose={() => setVersionsOpen(false)}
            />
          )}
          {/* Portalled: where it sits in the tree does not matter. */}
          {confirmReq && (
            <HpConfirmDialog
              /* A new question is a new dialog (fresh focus), even when it
                 replaces the last one in the same render — the medical
                 warning is followed straight by the publish confirm. */
              key={`${confirmReq.action}:${confirmReq.title}`}
              title={confirmReq.title}
              body={confirmReq.body}
              confirmLabel={confirmReq.confirmLabel}
              onConfirm={acceptConfirm}
              onCancel={cancelConfirm}
            />
          )}
        </aside>

        <main className="hp-main" ref={mainRef}>
          <div className="hp-content">
            {isNutrition && nutritionView === "guidelines" ? (
              <section className="hp-section">
                <div className="hp-sec-head">
                  <h2 className="hp-sec-title">Guidelines</h2>
                </div>
                <div className="hp-options-label">
                  <span>Options</span>
                </div>
                {guidelines.length === 0 && (
                  <p className="hp-empty-note">No guidelines on this version.</p>
                )}
                {guidelines.map((g, gIdx) => (
                  <div
                    className="hp-card"
                    key={`${g.name}-${gIdx}`}
                    {...dropProps("guideline", gIdx)}
                  >
                    <div className="hp-card-row">
                      <div className="hp-card-left">
                        <div className="hp-gd-head">
                          {!readOnly && (
                            <span
                              className="hp-grip"
                              title="Drag to reorder"
                              aria-hidden="true"
                              {...gripProps({ kind: "guideline", idx: gIdx })}
                            >
                              &#8942;&#8942;
                            </span>
                          )}
                          <span className="hp-gd-title">{g.name}</span>
                          {!readOnly && (
                            <button
                              type="button"
                              className="hp-gd-remove"
                              onClick={() => removeGuideline(gIdx)}
                            >
                              remove
                            </button>
                          )}
                        </div>
                        {/* The ranges and, at their right, the guideline's
                            image (200x128, Figma 3187:7269); the description
                            below keeps the full width. */}
                        <div className="hp-gd-media">
                          <div className="hp-qty-row">
                            <div className="hp-small-field w-236">
                              <span className="hp-field-label">Min range</span>
                              <input
                                className="hp-input"
                                value={g.minRange == null ? "" : g.minRange}
                                readOnly={locked}
                                // Bridge: digits and "." only.
                                onChange={(e) =>
                                  patchGuideline(gIdx, { minRange: e.target.value.replace(/[^0-9.]/g, "") })
                                }
                              />
                            </div>
                            <div className="hp-small-field w-236">
                              <span className="hp-field-label">Max range</span>
                              <input
                                className="hp-input"
                                value={g.maxRange == null ? "" : g.maxRange}
                                readOnly={locked}
                                onChange={(e) =>
                                  patchGuideline(gIdx, { maxRange: e.target.value.replace(/[^0-9.]/g, "") })
                                }
                              />
                            </div>
                            <div className="hp-small-field w-236">
                              <span className="hp-field-label">Unit</span>
                              <select
                                className="hp-input hp-unit-select"
                                value={g.unit || ""}
                                disabled={locked}
                                onChange={(e) => patchGuideline(gIdx, { unit: e.target.value })}
                              >
                                <option value="">Select unit</option>
                                {/* Bridge's fixed guideline units — not the
                                    ingredient units a recipe uses. */}
                                {GUIDELINE_UNITS.map((u) => (
                                  <option key={u} value={u}>
                                    {u}
                                  </option>
                                ))}
                                {g.unit && GUIDELINE_UNITS.indexOf(g.unit) === -1 && (
                                  <option value={g.unit}>{g.unit}</option>
                                )}
                              </select>
                            </div>
                          </div>
                          <HpImage src={g.image} alt={g.name} className="is-guideline" />
                        </div>
                        <DescLabel />
                        {readOnly ? (
                          <DescriptionHtml html={g.description} />
                        ) : (
                          <RichTextArea
                            key={`gd-${plan.id || "draft"}-${plan.current_level}-${planRev}-${gIdx}`}
                            // Seed SANITISED html — raw wire markup must never
                            // enter a live (editable) DOM.
                            defaultHtml={descHtml(g.description)}
                            placeholder="Describe this guideline…"
                            ariaLabel={`${g.name} description`}
                            disabled={busy}
                            onChange={(html) => patchGuideline(gIdx, { description: html })}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                ))}
                <AddGuideline
                  masters={guidelineMasters[activeCatId]}
                  existingNames={guidelines.map((g) => String(g.name || "").toLowerCase())}
                  disabled={readOnly}
                  inert={busy}
                  onAdd={addGuidelineFromMaster}
                />
              </section>
            ) : (
              /* ---- items view (all categories) ---- */
              <>
                {isTemplateTab && !readOnly && (
                  <TemplateSearch
                    categoryId={activeCatId}
                    inert={busy}
                    service={service}
                    onPick={applyTemplate}
                  />
                )}
                {/* Nutrition: the patient's food list first, as in legacy. */}
                {isNutrition && version > 2 && (
                  <HpFoodList
                    foodList={foodList}
                    status={foodListState}
                    open={foodListOpen}
                    onToggle={() => setFoodListOpen((o) => !o)}
                    onRetry={() => setFoodListState("idle")}
                  />
                )}
                {sections.length === 0 && (
                  <div className="hp-empty-block">
                    <p className="hp-empty-note">
                      Nothing on this tab yet{readOnly ? "." : " — add a section from the left."}
                    </p>
                    {/* The same generate, on an empty Nutrition tab — under
                        the same rule (editable, Version 3 up). */}
                    {isNutrition && !readOnly && version >= 3 && (
                      <button
                        type="button"
                        className="hp-btn hp-btn-primary"
                        disabled={busy}
                        onClick={generatePlan}
                      >
                        {generating ? "Generating…" : "Generate health plan"}
                      </button>
                    )}
                  </div>
                )}
                {sections.map((item, idx) => {
                  const secDomId = `hp-sec-${String(item.name).toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;
                  return (
                    <section
                      className="hp-section"
                      key={item.itemId || item.name}
                      id={secDomId}
                      {...dropProps("section", idx)}
                    >
                      <div className="hp-sec-head">
                        {!readOnly && (
                          <span
                            className="hp-grip"
                            title="Drag to reorder sections"
                            aria-hidden="true"
                            {...gripProps({ kind: "section", idx })}
                          >
                            &#8942;&#8942;
                          </span>
                        )}
                        <h2 className="hp-sec-title">{item.name}</h2>
                        {isNutrition && (
                          <input
                            className={`hp-time-pill hp-time-input${isTimeValid(item.time) ? "" : " is-invalid"}`}
                            placeholder="time"
                            aria-label={`${item.name} time`}
                            aria-invalid={!isTimeValid(item.time) || undefined}
                            maxLength={8}
                            value={item.time || ""}
                            readOnly={locked}
                            onChange={(e) => patchItem(idx, { time: e.target.value })}
                            title='e.g. "9:00 am"'
                          />
                        )}
                        {!readOnly && (
                          <button type="button" className="hp-remove" onClick={() => removeItem(idx)}>
                            Remove
                          </button>
                        )}
                      </div>
                      {routineTime(item.name) && (
                        <p className="hp-sec-routine">
                          In the patient&rsquo;s daily schedule at {routineTime(item.name)}
                        </p>
                      )}
                      <div className="hp-options-label">
                        <span>Options</span>
                      </div>

                      {(item.options || []).map((option, oIdx) => (
                        <div
                          className={`hp-card${!readOnly ? " is-removable" : ""}`}
                          key={`${option.name}-${oIdx}`}
                        >
                          {/* No drag handle on an option card, on any tab
                              (Nittin, 2026-09-17/18 — legacy reorders no
                              options); the remove control stays. */}
                          {!readOnly && (
                            <button
                              type="button"
                              className="hp-collapse"
                              title="Remove option"
                              aria-label="Remove option"
                              onClick={() => removeOption(idx, oIdx)}
                            >
                              −
                            </button>
                          )}
                          <div className="hp-card-row">
                            <div className="hp-card-left">
                              {(() => {
                                const flags = foodFlagsOf(option, foodIndex);
                                if (!option.auto && flags.length === 0) return null;
                                return (
                                  <div className="hp-card-tags">
                                    {option.auto && <span className="hp-auto">Auto Generated</span>}
                                    {flags.map((f) => (
                                      <span
                                        key={f.key}
                                        className={`hp-food-flag is-${f.tone}`}
                                        title={
                                          f.names.length
                                            ? `${f.label} for this patient: ${f.names.join(", ")}`
                                            : `${f.label} for this patient`
                                        }
                                      >
                                        {f.label}
                                      </span>
                                    ))}
                                  </div>
                                );
                              })()}
                              <span className="hp-field-label">name</span>
                              {/* Display-only, matching bridge: its option-name
                                  field is hardcoded disabled — the name comes
                                  from the picked master option, never retyped. */}
                              <input className="hp-input" value={option.name || ""} readOnly />
                              <DescLabel />
                              {readOnly ? (
                                <DescriptionHtml html={option.description} />
                              ) : (
                                <RichTextArea
                                  key={`op-${plan.id || "draft"}-${plan.current_level}-${planRev}-${item.itemId || idx}-${oIdx}`}
                                  defaultHtml={descHtml(option.description)}
                                  placeholder="Describe this option…"
                                  ariaLabel={`${option.name || "option"} description`}
                                  disabled={busy}
                                  onChange={(html) => patchOption(idx, oIdx, { description: html })}
                                />
                              )}
                              {(option.recipes || []).length > 0 && (
                                <OptionRecipes
                                  recipes={option.recipes}
                                  idBase={`hp-rc-${idx}-${oIdx}`}
                                  renderRecipe={(r, rIdx) => (
                                    <RecipeBlock
                                      recipe={r}
                                      readOnly={readOnly}
                                      disabled={busy}
                                      units={units}
                                      ingredientMasters={ingredientMasters}
                                      editKey={`rc-${plan.id || "draft"}-${plan.current_level}-${planRev}-${item.itemId || idx}-${oIdx}-${rIdx}`}
                                      onPatch={(patch) => patchRecipe(idx, oIdx, rIdx, patch)}
                                      onPatchIngredient={(gi, patch) =>
                                        patchIngredient(idx, oIdx, rIdx, gi, patch)
                                      }
                                      onAddIngredient={(ing) => addIngredient(idx, oIdx, rIdx, ing)}
                                      onRemoveIngredient={(gi) =>
                                        removeIngredient(idx, oIdx, rIdx, gi)
                                      }
                                      service={service}
                                    />
                                  )}
                                />
                              )}
                            </div>
                            {/* Exercise / Medical / Meditation options carry their
                                image at the card's right (250x150, Figma
                                3187:6561); a Nutrition option has none, in
                                legacy and on the board. Where the card has its
                                drag + remove controls in that corner, the image
                                starts below them instead of under them. */}
                            {!isNutrition && (
                              <HpImage
                                src={(option.images || [])[0]}
                                alt={option.name}
                                className={`is-option${readOnly ? "" : " is-under-controls"}`}
                              />
                            )}
                          </div>
                        </div>
                      ))}

                      <AddOption
                        itemId={item.itemId}
                        disabled={readOnly}
                        inert={busy}
                        onAdd={(opt) => addOption(idx, item.itemId, opt)}
                        service={service}
                      />
                    </section>
                  );
                })}
              </>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

HealthPlan.propTypes = {
  match: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired,
};

export default HealthPlan;
